/**
 * Auth Initialization Hook (Story 1.6)
 *
 * Handles initial authentication state on app load:
 * 1. Extracts token from OAuth redirect URL fragment (if present)
 * 2. Attempts silent refresh using HttpOnly cookie
 * 3. Sets loading state while auth is being determined
 *
 * IMPORTANT: This hook should be used at the app root level,
 * typically in the root route or App component.
 */

import { useEffect, useState, useRef } from "react";
import { useAuthStore } from "../stores/authStore";
import {
  refreshAccessToken,
  extractTokenFromHash,
  clearHashFromUrl,
} from "../lib/auth";

// Module-level flags to prevent double initialization in React Strict Mode
// This is necessary because token rotation invalidates tokens on use
let authInitStarted = false;
let authInitCompleted = false;

// Reset flags for HMR (Hot Module Replacement) in development
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    authInitStarted = false;
    authInitCompleted = false;
  });
}

/**
 * Reset the auth init flag (for testing only)
 * @internal
 */
export function __resetAuthInitFlag(): void {
  authInitStarted = false;
  authInitCompleted = false;
}

/**
 * Auth initialization result
 */
interface AuthInitResult {
  /** True while auth state is being determined */
  isLoading: boolean;
  /** True if user is authenticated after init */
  isAuthenticated: boolean;
  /** Error message if init failed */
  error: string | null;
}

/**
 * Hook to initialize authentication state on app load
 *
 * This hook handles:
 * 1. OAuth callback - extracts token from URL fragment
 * 2. Silent refresh - uses HttpOnly cookie to get new tokens
 * 3. Loading state - prevents route guards from redirecting prematurely
 *
 * @returns Auth initialization state
 *
 * @example
 * ```typescript
 * // In App.tsx or root route
 * function App() {
 *   const { isLoading, isAuthenticated } = useAuthInit();
 *
 *   if (isLoading) {
 *     return <LoadingSpinner />;
 *   }
 *
 *   return <RouterProvider router={router} />;
 * }
 * ```
 */
export function useAuthInit(): AuthInitResult {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initRef = useRef(false);

  const { setLoading, getIsAuthenticated } = useAuthStore();

  useEffect(() => {
    // If init already completed (e.g. HMR re-mount), just clear loading
    if (authInitCompleted) {
      setLoading(false);
      setIsLoading(false);
      return;
    }

    // Guard against React Strict Mode double-invocation
    // This is critical because token rotation invalidates tokens on use
    if (authInitStarted || initRef.current) {
      return;
    }
    authInitStarted = true;
    initRef.current = true;

    async function initAuth() {
      try {
        // Check for OAuth redirect with token in hash
        if (window.location.hash.includes("access_token")) {
          const extracted = extractTokenFromHash(window.location.hash);
          if (extracted) {
            // Clear token from URL for security
            clearHashFromUrl();
            setLoading(false);
            setIsLoading(false);
            return;
          }
        }

        // Attempt silent refresh using HttpOnly cookie
        const token = await refreshAccessToken();

        if (token) {
          // Successfully refreshed - user is authenticated
          setLoading(false);
        } else {
          // No valid session - user needs to login
          setLoading(false);
        }
      } catch (err) {
        console.error("[AuthInit] Error initializing auth:", err);
        setError("Failed to initialize authentication");
        setLoading(false);
      } finally {
        authInitCompleted = true;
        setIsLoading(false);
      }
    }

    initAuth();
  }, [setLoading]);

  return {
    isLoading,
    isAuthenticated: getIsAuthenticated(),
    error,
  };
}

/**
 * Hook to handle OAuth redirect callback
 *
 * Use this on pages that may receive OAuth redirects
 * (e.g., dashboard) to extract and store the token.
 *
 * @example
 * ```typescript
 * function DashboardPage() {
 *   useOAuthCallback();
 *   // ... rest of component
 * }
 * ```
 */
export function useOAuthCallback(): void {
  useEffect(() => {
    if (window.location.hash.includes("access_token")) {
      const extracted = extractTokenFromHash(window.location.hash);
      if (extracted) {
        clearHashFromUrl();
      }
    }
  }, []);
}
